import api from 'api/apiClient';
import { PRIORITY_KEYMAP } from 'modules/shared/constants';
import {
  calculateProjectTimeLeft,
  formatTimeLeft,
  getProjectPriorityScore,
  sortTasksByPriority,
} from '../utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export async function getProjectsByWeek(employeeId, weekNo) {
  if (!employeeId || !weekNo) throw new Error('Employee id and week number are required');
  const { data: raw } = await unwrap(await api.get(`/api/employee/${employeeId}/jobs/${weekNo}`));

  const projects = raw.map((project) => {
    const tasks = sortTasksByPriority(
      project.tasks.map((task) => ({
        id: task.id,
        name: task.name,
        priority: PRIORITY_KEYMAP[task.priority],
        totalTime: task.alloted_time,
        timeUsed: task.time_used,
        currentWorkingStatus: task.current_working_status,
        lastPlay: task.last_play,
        lastPause: task.last_pause,
        note: task.note,
        documentsShared: task.documents_shared,
      })),
    );

    const timeLeft = calculateProjectTimeLeft(tasks, new Date());

    return {
      id: project.job_id,
      code: project.job_code,
      name: project.job_name,
      status: project.job_status,
      department: project.department,
      tasks,
      _priorityScore: getProjectPriorityScore(tasks),
      timeLeft: formatTimeLeft(timeLeft),
    };
  });

  return projects
    .sort((a, b) => b._priorityScore - a._priorityScore)
    .map(({ _priorityScore, ...project }) => project);
}

/**
 * Updates task working state.
 *
 * @param {number} taskId
 * @param {Object} payload
 * @returns {Promise<void>}
 */
export async function updateTaskStatus(employeeId, taskId, data) {
  if (!employeeId || !taskId || !data)
    throw new Error('Employee id, task id and data are required');

  const payload: Record<string, any> = {
    current_working_status: data.currentWorkingStatus,
  };

  if (data.intent === 'PLAY') {
    payload.last_play = data.lastPlay;
  } else if (data.intent === 'PAUSE') {
    payload.time_used = data.timeUsed;
    payload.last_play = null;
  }

  await api.put(`/api/employee/${employeeId}/task/${taskId}`, payload);
}
